import React, { ChangeEvent, KeyboardEvent, DetailedHTMLProps, HTMLAttributes, ReactHTML, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { LoadingState } from 'src/pages/customer-center/data/types/DataLoading';
import { useBootstrapTooltip } from 'src/components/hooks';
import { stripNewLines } from 'src/utils/text';
import { useAutosizeTextArea, useAutoWidthInput } from 'src/utils/hooks/ui';

export type CheckboxProps = JSX.IntrinsicElements['input'] & {
    stateChanged: (state: boolean) => void;
    labelAfter: ReactNode;
}

function mergeClassNames(builtIn: string, props: string) {
    if (props) {
        return builtIn + ' ' + props;
    } else {
        return builtIn;
    }
}

export function FormGroup(props: JSX.IntrinsicElements['div']) {
    return <div {...props} className={mergeClassNames('form-group', props.className)}>
        {props.children}
    </div>
}

export function InputGroup(props: JSX.IntrinsicElements['div']) {
    return <div {...props} className={mergeClassNames('input-group', props.className)}>
        {props.children}
    </div>
}
export function InputGroupAppend(props: JSX.IntrinsicElements['div']) {
    return <div className={mergeClassNames('input-group-append', props.className)} {...props}>
        {props.children}
    </div>
}
export function InputGroupPrepend(props: JSX.IntrinsicElements['div']) {
    return <div className={mergeClassNames('input-group-prepend', props.className)} {...props}>
        {props.children}
    </div>
}
export function InputGroupText(props: JSX.IntrinsicElements['div']) {
    return <div className={mergeClassNames('input-group-text', props.className)} {...props}>
        {props.children}
    </div>
}

export function InputText(props: JSX.IntrinsicElements['input']) {
    return <input {...props} type='text' className={mergeClassNames('form-control', props.className)} />
}

export function InputNumber(props: JSX.IntrinsicElements['input']) {
    return <input {...props} type='number' className={mergeClassNames('form-control', props.className)} />
}

export function InputPhone(props: JSX.IntrinsicElements['input']) {
    return <input {...props} type='tel' className={mergeClassNames('form-control', props.className)} />
}

export function InputTextAutowidth(props: JSX.IntrinsicElements['input'] & { minWidth?: number }) {
    const { minWidth, ...allProps } = props;
    const inputRef = useAutoWidthInput(props.value as string, minWidth);

    return <input {...allProps} ref={inputRef} type='text' className={mergeClassNames('form-control', props.className)} />
}

export function InputPassword(props: JSX.IntrinsicElements['input']) {
    return <input {...props} type='password' className={mergeClassNames('form-control', props.className)} />
}

export function TextArea(props: JSX.IntrinsicElements['textarea']) {
    return <textarea {...props} className={mergeClassNames('form-control', props.className)} />
}

export function TextAreaSingleLine(props: { className?: string, value?: string, onValChange?: (val?: string) => void, autoFocus?: boolean }) {
    const onChange = useCallback((e: ChangeEvent<HTMLDivElement>) => {
        // const newEv = {
        //     ...e,
        //     target: {
        //         ...e.target,
        //         value: stripNewLines(e.target.innerHTML)
        //     }
        // }
        console.log('AAA', e.target.innerHTML, stripNewLines(e.target.innerHTML));

        props?.onValChange(stripNewLines(e.target.innerHTML));
    }, [props.onValChange]);

    const onkeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key == 'Enter') {
            e.preventDefault();
            e.stopPropagation();
        }
    }, []);

    // const textAreaRef = useRef<HTMLTextAreaElement>(null);
    // const inputRef = useRef<HTMLInputElement>(null);

    // useAutosizeTextArea(textAreaRef.current, inputRef.current, props.value as string);

    const r = useRef<HTMLDivElement>();

    useEffect(() => {
        if (props.autoFocus && r.current) {
            r.current.focus();
        }
    }, [props.autoFocus]);

    useEffect(() => {
        if (props.value) {
            r.current.textContent = props.value;
        }
    }, [props.value]);

    return <div
        //@ts-ignore
        contentEditable='plaintext-only'
        ref={r}
        onInput={onChange}
        onKeyDown={onkeyDown}
        suppressContentEditableWarning={true}
        className={mergeClassNames('form-control single-line-area', props.className)}></div>
}

export function InputCheckbox(props: CheckboxProps) {
    const [state, setState] = useState(props.checked);

    const updateState = () => {
        setState(!state);

        props.stateChanged(!state);
    }

    const newProps = { ...props };
    delete newProps.stateChanged;

    //return <input {...newProps} type='checkbox' className={mergeClassNames('form-control', props.className)} checked={state} onChange={(e) => updateState()} />
    return <label className="pfcheckbox mb-0">
        <input type="checkbox" checked={state} onChange={(e) => updateState()} />
        <span className="checkmark"></span>
        <span className="label-after">{props.labelAfter}</span>
    </label>
}

export function Select(props: JSX.IntrinsicElements['select']) {
    return <select {...props} className={mergeClassNames('form-control', props.className)}>
        {props.children}
    </select>
}

export function Label(props: { children: React.ReactNode, className?: string }) {
    return <label className={`text-neutral-600 dark:text-slate-100 text-sm mb-1 inline-block ${props.className}`}>{props.children}</label>;
}

function ButtonInternal(props: JSX.IntrinsicElements['button'] & { loadingState?: LoadingState, isWorking?: boolean, tooltip?: string, hideChildrenWhenWorking?: boolean }) {
    const { loadingState, isWorking, hideChildrenWhenWorking, ...changedProps } = props;
    const ref = useBootstrapTooltip();

    const isDoingWork = isWorking || loadingState == 'loading';
    const shouldChildrenBeVisible = () => {
        if (hideChildrenWhenWorking == true) {
            return !isDoingWork;
        } else {
            return true;
        }
    }

    const style = Object.assign(props.style || {}, (isWorking || props.disabled) ? { pointerEvents: "none" } : {});

    return <button {...changedProps} disabled={isWorking || props.disabled} title={props.tooltip} ref={ref} style={style}>
        {(loadingState || isWorking) && <span className="spinner-grow spinner-grow-sm" style={{ display: isDoingWork ? 'inline-block' : 'none' }}></span>}
        {shouldChildrenBeVisible() && props.children}
    </button>
}

export function ButtonPrimary(props: JSX.IntrinsicElements['button'] & { loadingState?: LoadingState, isWorking?: boolean, hideChildrenWhenWorking?: boolean }) {
    return <ButtonInternal {...props} className={mergeClassNames('btn btn-primary', props.className)}>{props.children}</ButtonInternal>
}

export function ButtonSecondary(props: JSX.IntrinsicElements['button'] & { loadingState?: LoadingState, isWorking?: boolean, hideChildrenWhenWorking?: boolean }) {
    return <ButtonInternal {...props} className={mergeClassNames('btn btn-secondary', props.className)}>{props.children}</ButtonInternal>
}

export function ButtonSuccess(props: JSX.IntrinsicElements['button'] & { loadingState?: LoadingState, isWorking?: boolean, hideChildrenWhenWorking?: boolean }) {
    return <ButtonInternal {...props} className={mergeClassNames('btn btn-success', props.className)}>{props.children}</ButtonInternal>
}

export function ButtonDanger(props: JSX.IntrinsicElements['button'] & { loadingState?: LoadingState, isWorking?: boolean, hideChildrenWhenWorking?: boolean }) {
    return <ButtonInternal {...props} className={mergeClassNames('btn btn-danger', props.className)}>{props.children}</ButtonInternal>
}

export function ButtonTransparent(props: JSX.IntrinsicElements['button'] & { loadingState?: LoadingState, isWorking?: boolean, tooltip?: string, hideChildrenWhenWorking?: boolean }) {
    return <ButtonInternal {...props} className={mergeClassNames('btn btn-transparent', props.className)}>{props.children}</ButtonInternal>
}

export function LinkButton(props: JSX.IntrinsicElements['span']) {
    return <span {...props} className={mergeClassNames('text-underline cursor-pointer', props.className)}>{props.children}</span>
}

export function InputSwitch(props: JSX.IntrinsicElements['input'] & { text?: React.ReactNode, notBold?: boolean, regularSize?: boolean, regularFont?: boolean }) {
    const { text, regularSize, regularFont, notBold, ...inputProps } = props;

    return <label className={`pfswitch mb-0 ${regularSize ? 'size-regular' : ''}`}>
        <input type="checkbox" {...inputProps} />
        <span className="checkmark"></span>
        <span className={`label-after ${notBold ? '' : 'font-weight-bold'}`} style={{ fontSize: props.regularFont ? '1rem' : '' }}>{text}</span>
    </label>
}
